/** Clear the embedded portal's session storage before removing its iframe. */
export function closeEmployeePreviewFrame(
  iframe: HTMLIFrameElement | null,
  onClosed: () => void
) {
  const frameWindow = iframe?.contentWindow;
  if (!frameWindow || !iframe?.src) {
    onClosed();
    return;
  }

  const channel = new MessageChannel();
  let completed = false;
  const finish = () => {
    if (completed) return;
    completed = true;
    window.clearTimeout(timeout);
    channel.port1.close();
    channel.port2.close();
    onClosed();
  };
  const timeout = window.setTimeout(finish, 500);
  channel.port1.onmessage = ({ data }) => {
    if (data?.type === "employee-preview-cleared") finish();
  };
  try {
    frameWindow.postMessage(
      { type: "employee-preview-close" },
      new URL(iframe.src).origin,
      [channel.port2]
    );
  } catch {
    finish();
  }
}

/** Renew only the employee currently displayed in the active preview iframe. */
export function listenForEmployeePreviewRenewal(
  getFrame: () => HTMLIFrameElement | null,
  employeeId: string,
  issueToken: (employeeId: string) => Promise<string>,
  isClosing: () => boolean
): () => void {
  let active = true;
  let pending: Promise<string> | null = null;
  const handleMessage = (event: MessageEvent) => {
    if (
      !active ||
      isClosing() ||
      event.data?.type !== "employee-preview-renew" ||
      event.data.employeeId !== employeeId ||
      !event.ports[0]
    )
      return;
    const iframe = getFrame();
    if (
      !iframe?.src ||
      event.source !== iframe.contentWindow ||
      event.origin !== new URL(iframe.src).origin
    )
      return;

    const port = event.ports[0];
    if (!pending) {
      pending = issueToken(employeeId).finally(() => {
        pending = null;
      });
    }
    pending.then(
      (accessToken) => {
        if (
          active &&
          !isClosing() &&
          getFrame() === iframe &&
          event.source === iframe.contentWindow
        ) {
          port.postMessage({ type: "employee-preview-renewed", accessToken });
        }
      },
      () => {
        if (
          active &&
          !isClosing() &&
          getFrame() === iframe &&
          event.source === iframe.contentWindow
        ) {
          port.postMessage({ type: "employee-preview-renew-failed" });
        }
      }
    );
  };
  window.addEventListener("message", handleMessage);
  return () => {
    active = false;
    window.removeEventListener("message", handleMessage);
  };
}
